import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare } from "lucide-react";
import { useRangeQuery } from "@/lib/useData";
import { useAuth } from "@/lib/auth";
import { Card, SectionTitle, Takeaway, LoadingBlocks, ErrorState, Forbidden } from "@/components/shared/primitives";
import { PriorityBadge, SegmentedControl, QuickFilterChips, DeltaBadge } from "@/components/kit";
import { pct, money, num, shortDate, daysUntil, CATEGORY_LABELS } from "@/lib/format";

const KAWAII = "#788CE3", FLORAL = "#92BAD5", NAVY = "#17243F", SUNKEN = "#EFEADA", BORDER = "#E3DED0";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}
function weekLabel(iso) { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
function dueLabel(days) {
  if (days == null) return "";
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

/* ---- Figure strip visuals ---- */
function Sparkline({ values }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1), min = Math.min(...values, 0);
  const w = 120, h = 36;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / (max - min || 1)) * h}`).join(" ");
  return <svg width={w} height={h} className="mt-3"><polyline fill="none" stroke={KAWAII} strokeWidth="2.5" points={pts} strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function Waffle({ active, total }) {
  return (
    <div className="mt-3 flex flex-wrap gap-[3px]" style={{ width: 20 * 10 }}>
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} style={{ width: 7, height: 7, borderRadius: 2, background: i < active ? KAWAII : SUNKEN }} />
      ))}
    </div>
  );
}

function FigureCol({ label, figure, context, visual, to }) {
  return (
    <Link to={to} className="flex-1 px-6 py-5 block hover:bg-[var(--bg-selected)] transition-colors first:rounded-l-2xl last:rounded-r-2xl">
      <div className="nb-label">{label}</div>
      <div className="font-display text-[36px] leading-[40px] font-bold text-nb-ink tnum mt-1">{figure}</div>
      <div className="text-[13px] text-nb-ink2 mt-1">{context}</div>
      {visual}
    </Link>
  );
}

/* ---- Spend against value bar ---- */

export default function Overview() {
  const { member } = useAuth();
  const { data, isLoading, error, refetch } = useRangeQuery("overview", "/overview");
  const [prioFilter, setPrioFilter] = useState("all");
  const [seatView, setSeatView] = useState("idle_cost");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [spendDim, setSpendDim] = useState("tool");
  const [activeDim, setActiveDim] = useState("team");

  const d = data?.data;

  const trend = useMemo(() => (d?.trend || []).map((t, i, arr) => ({
    label: weekLabel(t.week), active_users: t.active_users,
    delta: i > 0 ? t.active_users - arr[i - 1].active_users : null,
  })), [d]);

  const alerts = d?.alerts_full || [];
  const shownAlerts = prioFilter === "all" ? alerts : alerts.filter((a) => {
    if (prioFilter === "Renewal") return a.type === "Renewal";
    if (prioFilter === "Idle seats") return a.type === "Idle seats";
    if (prioFilter === "Adoption") return a.type === "Adoption";
    return true;
  });
  const counts = useMemo(() => ({
    all: alerts.length,
    Renewal: alerts.filter((a) => a.type === "Renewal").length,
    "Idle seats": alerts.filter((a) => a.type === "Idle seats").length,
    Adoption: alerts.filter((a) => a.type === "Adoption").length,
  }), [alerts]);

  if (error?.code === "forbidden") return <Forbidden who="Admins and IT Admins" />;
  if (isLoading) return <LoadingBlocks rows={5} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!d) return null;

  const firm = d.firm;
  const glance = d.glance || [];
  const topAlert = alerts[0];
  const topTool = glance.find((g) => g.id === topAlert?.tool_id) || glance[0];

  const idleSeats = firm.seat_utilisation.total_seats - firm.seat_utilisation.active_seats;
  const spend = firm.monthly_spend.value;
  const idleCost = firm.idle_seat_cost.value;
  const usedCost = spend - idleCost;
  const idlePctOfSpend = Math.round((idleCost / spend) * 100);
  const renew45 = glance.filter((g) => g.days_to_renewal != null && g.days_to_renewal >= 0 && g.days_to_renewal <= 45).length;

  const runway = [...glance].filter((g) => g.days_to_renewal != null).sort((a, b) => a.days_to_renewal - b.days_to_renewal);

  const seatRows = [...glance].sort((a, b) => {
    if (seatView === "idle_cost") return b.idle_seat_cost - a.idle_seat_cost;
    if (seatView === "utilisation") return a.utilisation - b.utilisation;
    return b.seats_bought - a.seats_bought;
  });
  const maxSeats = Math.max(...glance.map((g) => g.seats_bought), 1);

  const teams = d.teams_all || [];
  const selTeam = teams.find((t) => t.team_id === selectedTeam)
    || [...teams].filter((t) => !t.group_too_small).sort((a, b) => a.adoption - b.adoption)[0];
  const lowestTeam = [...teams].filter((t) => !t.group_too_small).sort((a, b) => a.adoption - b.adoption)[0];

  const firstName = (member?.name || "there").split(" ")[0];
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6">
      {/* 1. Greeting row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 pt-11">
        <div className="lg:col-span-3">
          <div className="nb-label">{today} · last 30 days</div>
          <h1 className="font-display font-extrabold text-nb-ink mt-2" style={{ fontSize: 48, lineHeight: "52px" }}>{greeting()}, {firstName}.</h1>
          <p className="mt-4 text-[19px] leading-[30px] text-nb-ink2 max-w-[640px]">
            <b className="text-nb-ink">{num(firm.active_users.value)} people</b> used an AI tool this month, which is <b className="text-nb-ink">{pct(firm.people_using_ai.value)}</b> of the firm. But <b className="text-nb-ink">{idleSeats} of {firm.seat_utilisation.total_seats}</b> paid seats went unused, costing <b className="text-nb-ink">{money(idleCost)}</b> a month, or {idlePctOfSpend}% of AI spend. {renew45 >= 1 ? `${renew45 === 3 ? "Three" : renew45} renewal${renew45 === 1 ? "" : "s"} land in the next 45 days.` : ""}
          </p>
        </div>
        {(topAlert && topTool) || lowestTeam ? (
          <div className="lg:col-span-2 flex flex-col gap-4">
            {topAlert && topTool && (
              <div className="rounded-2xl p-6" style={{ background: "var(--brand-lime)", color: NAVY }} data-testid="next-decision">
                <div className="flex items-center justify-between">
                  <span className="nb-label" style={{ color: NAVY, opacity: 0.8 }}>Next decision · renewal</span>
                  <span className="text-[12px] font-semibold rounded-md px-2 py-0.5" style={{ background: NAVY, color: "#F7F3E6" }}>Due {dueLabel(topTool.days_to_renewal)}</span>
                </div>
                <h2 className="font-display font-bold mt-3" style={{ fontSize: 22, lineHeight: "26px" }}>{topTool.name} renews on {shortDate(topTool.renewal_date)}</h2>
                <p className="text-[14px] mt-2 leading-relaxed">
                  {topTool.active_users} of {topTool.seats_bought} seats in use ({pct(topTool.utilisation)}) at {money(topTool.cost_per_active_user)} per active user.
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <Link to={`/apps/${topTool.id}`} className="h-10 inline-flex items-center px-4 rounded-[10px] font-semibold text-[14px]" style={{ background: NAVY, color: "#F7F3E6" }} data-testid="review-renewal">Review renewal →</Link>
                  <Link to="/review" className="text-[14px] font-medium" style={{ color: "var(--text-link)" }}>Ask the {topTool.active_users} users</Link>
                </div>
              </div>
            )}
            {lowestTeam && (
              <div className="rounded-2xl p-6 bg-nb-surface" style={{ border: "1px solid var(--border-subtle)" }} data-testid="next-adoption">
                <div className="flex items-center justify-between">
                  <span className="nb-label">Next decision · adoption</span>
                  <span className="text-[12px] font-semibold rounded-md px-2 py-0.5 text-nb-ink" style={{ background: FLORAL }}>{pct(lowestTeam.adoption)} adoption</span>
                </div>
                <h2 className="font-display font-bold mt-3 text-nb-ink" style={{ fontSize: 22, lineHeight: "26px" }}>{lowestTeam.team} is the firm's lowest-adopting team</h2>
                <p className="text-[14px] mt-2 leading-relaxed text-nb-ink2">
                  {lowestTeam.members - lowestTeam.members_using} of {lowestTeam.members} people have not started yet, against the firm's {pct(d.firm_people)}.
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <Link to={`/teams/${lowestTeam.team_id}`} className="h-10 inline-flex items-center px-4 rounded-[10px] font-semibold text-[14px] bg-nb-navy text-nb-cream" data-testid="review-adoption">See the team →</Link>
                  <Link to="/review" className="text-[14px] font-medium" style={{ color: "var(--text-link)" }}>Send a questionnaire</Link>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* 2. Figure strip */}
      <Card className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x" style={{ borderColor: BORDER }} data-testid="figure-strip">
        <FigureCol label="Active users" to="/people" figure={num(firm.active_users.value)}
          context={<DeltaBadge change={firm.active_users.change} higherIsBetter />}
          visual={<Sparkline values={trend.map((t) => t.active_users)} />} />
        <FigureCol label="People using AI" to="/people" figure={pct(firm.people_using_ai.value)}
          context={`${firm.people_using_ai.active_users} of ${firm.people_using_ai.total_members} people, any tool`}
          visual={<Waffle active={firm.people_using_ai.active_users} total={firm.people_using_ai.total_members} />} />
        <FigureCol label="Seat utilisation" to="/apps" figure={pct(firm.seat_utilisation.value)}
          context={`${firm.seat_utilisation.active_seats} of ${firm.seat_utilisation.total_seats} paid seats active`}
          visual={<div className="mt-4 h-2 rounded-full" style={{ background: SUNKEN }}><div className="h-full rounded-full" style={{ width: pct(firm.seat_utilisation.value), background: KAWAII }} /></div>} />
        <FigureCol label="Monthly AI spend" to="/reports" figure={money(spend)}
          context={<DeltaBadge change={firm.monthly_spend.change} higherIsBetter={false} />}
          visual={<div className="mt-4"><div className="h-2 rounded-full overflow-hidden flex" style={{ background: SUNKEN }}><div style={{ width: `${(usedCost / spend) * 100}%`, background: NAVY }} /><div style={{ width: `${(idleCost / spend) * 100}%`, background: FLORAL }} /></div><div className="text-[12px] text-nb-ink2 mt-1.5">{money(usedCost)} used · {money(idleCost)} on idle seats</div></div>} />
      </Card>

      {/* 3. Weekly active + What needs you */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2" data-testid="active-users-panel">
          <div className="flex items-center justify-between">
            <SectionTitle>Active users</SectionTitle>
            <SegmentedControl value={activeDim} onChange={setActiveDim} testid="active-dim"
              options={[{ value: "team", label: "By team" }, { value: "department", label: "By department" }, { value: "location", label: "By location" }]} />
          </div>
          <Takeaway>People who used any AI tool in the period, grouped by {activeDim}. The bar shows active users; the figure shows active of total.</Takeaway>
          {(() => {
            let bd = ((d.active_breakdown || {})[activeDim]) || [];
            // Fallback for older payloads that lack active_breakdown: derive the team view from teams_all.
            if (bd.length === 0 && activeDim === "team") {
              bd = (d.teams_all || []).filter((t) => !t.group_too_small)
                .map((t) => ({ name: t.team, active: t.members_using, total: t.members }));
            }
            const rows = [...bd].sort((a, b) => b.active - a.active);
            const max = Math.max(...rows.map((r) => r.active), 1);
            if (rows.length === 0) return <p className="text-[14px] text-nb-ink2 mt-4" data-testid="active-empty">No active users recorded for this view.</p>;
            return (
              <div className="space-y-2.5 mt-2">
                {rows.map((r) => {
                  const w = (r.active / max) * 100;
                  const p = r.total ? Math.round((r.active / r.total) * 100) : 0;
                  return (
                    <div key={r.name} className="flex items-center gap-3" data-testid={`active-row-${r.name}`}>
                      <span className="w-[160px] shrink-0 text-[14px] text-nb-ink truncate">{r.name}</span>
                      <span className="flex-1 h-6 rounded-md relative" style={{ background: SUNKEN }}>
                        <span className="absolute inset-y-0 left-0 rounded-md transition-all" style={{ width: `${w}%`, background: KAWAII }} />
                      </span>
                      <span className="w-[140px] shrink-0 text-right text-[13px] text-nb-ink2 tnum">{r.active} of {r.total} · {p}%</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Card>

        <Card className="p-6" data-testid="what-needs-you">
          <SectionTitle>What needs you</SectionTitle>
          <Takeaway>{counts.all} actions, grouped from 12 alerts.</Takeaway>
          <div className="mb-4">
            <QuickFilterChips
              value={prioFilter}
              onChange={setPrioFilter}
              chips={[
                { key: "all", label: "All", count: counts.all },
                { key: "Renewal", label: "Renewals", count: counts.Renewal },
                { key: "Idle seats", label: "Idle seats", count: counts["Idle seats"] },
                { key: "Adoption", label: "Adoption", count: counts.Adoption },
              ]}
            />
          </div>
          <ul className="space-y-3">
            {shownAlerts.map((a) => (
              <li key={a.id} className="flex items-start gap-3" data-testid={`alert-${a.type}`}>
                <PriorityBadge priority={a.priority} />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-nb-ink">{a.title}</div>
                  <div className="text-[13px] text-nb-ink2">{a.detail}</div>
                  <Link to={a.link} className="text-[13px] font-medium" style={{ color: "var(--text-link)" }}>{a.action} →</Link>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* 4. Spend against value */}
      <Card className="p-6" data-testid="spend-value">
        <div className="flex items-center justify-between">
          <SectionTitle>Spend against value</SectionTitle>
          <SegmentedControl value={spendDim} onChange={setSpendDim} testid="spend-dim"
            options={[{ value: "tool", label: "By tool" }, { value: "team", label: "By team" }, { value: "category", label: "By category" }]} />
        </div>
        <Takeaway>Monthly spend as the bar; the badge shows how much value that spend buys. Longer bars with a low badge are the ones to review.</Takeaway>
        {(() => {
          const ss = d.spend_snapshot || {};
          const catAgg = {};
          glance.forEach((g) => {
            const c = catAgg[g.category] || { cost: 0, active: 0, seats: 0 };
            c.cost += g.monthly_cost; c.active += g.active_users; c.seats += g.seats_bought;
            catAgg[g.category] = c;
          });
          let data;
          if (spendDim === "tool") {
            data = glance.map((g) => ({ key: g.id, name: g.name, cost: g.monthly_cost, value: g.utilisation,
              valLabel: `${pct(g.utilisation)} used`, used: Math.max(0, g.monthly_cost - g.idle_seat_cost), idle: g.idle_seat_cost, to: `/apps/${g.id}` }));
          } else if (spendDim === "team") {
            data = (ss.by_team || []).map((t) => { const tm = teams.find((x) => x.team_id === t.id); const v = tm ? tm.adoption : 0; return {
              key: t.id, name: t.name, cost: t.cost, value: v,
              valLabel: tm ? `${pct(v)} adoption` : "—", used: t.cost * v, idle: t.cost * (1 - v), to: `/teams/${t.id}` }; });
          } else {
            data = Object.entries(catAgg).map(([cat, v]) => { const val = v.seats ? v.active / v.seats : 0; return {
              key: cat, name: CATEGORY_LABELS[cat] || cat, cost: v.cost, value: val,
              valLabel: `${pct(val)} used`, used: v.cost * val, idle: v.cost * (1 - val) }; });
          }
          data = data.filter((x) => x.cost > 0).sort((a, b) => b.cost - a.cost);
          const maxCost = Math.max(...data.map((s) => s.cost), 1);
          if (data.length === 0) return <p className="text-[14px] text-nb-ink2 mt-4">No spend recorded for this view.</p>;
          const Row = ({ r }) => {
            const w = (r.cost / maxCost) * 100;
            const lowValue = r.value != null && r.value < 0.55;
            const inner = (
              <>
                <div className="w-[150px] shrink-0 text-[14px] text-nb-ink truncate">{r.name}</div>
                <div className="flex-1 h-6 rounded-md relative" style={{ background: "var(--bg-sunken)" }}>
                  <div className="absolute inset-y-0 left-0 h-full rounded-md overflow-hidden flex" style={{ width: `${w}%` }}>
                    <div style={{ width: `${(r.used / r.cost) * 100}%`, background: NAVY }} />
                    <div style={{ width: `${(r.idle / r.cost) * 100}%`, background: FLORAL }} />
                  </div>
                </div>
                <div className="w-[110px] shrink-0 text-right text-[13px] text-nb-ink tnum">{money(r.cost)}</div>
                <div className="w-[120px] shrink-0 text-right">
                  <span className="text-[12px] font-medium rounded-md px-2 py-0.5 text-nb-ink" style={{ background: lowValue ? FLORAL : "var(--bg-sunken)" }}>{r.valLabel}</span>
                </div>
              </>
            );
            return r.to
              ? <Link to={r.to} className="flex items-center gap-3 py-1 rounded-md hover:bg-[var(--bg-selected)] transition-colors" data-testid={`spend-row-${r.key}`}>{inner}</Link>
              : <div className="flex items-center gap-3 py-1" data-testid={`spend-row-${r.key}`}>{inner}</div>;
          };
          return (
            <div className="mt-2">
              <div className="flex items-center gap-4 text-[12px] text-nb-ink2 mb-3">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: NAVY }} /> Spend being used</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: FLORAL }} /> Spend not yet used</span>
              </div>
              <div className="space-y-2">
                {data.map((r) => <Row key={r.key} r={r} />)}
              </div>
            </div>
          );
        })()}
      </Card>

      {/* 5. Renewal runway */}
      <Card className="p-6" data-testid="renewal-runway">
        <div className="flex items-center justify-between">
          <SectionTitle>Renewal runway</SectionTitle>
          <Link to="/reports" className="text-[13px] font-medium inline-flex items-center gap-1" style={{ color: "var(--text-link)" }}>Renewal report <ArrowRight size={13} /></Link>
        </div>
        <Takeaway>{runway.filter((g) => g.days_to_renewal <= 90).length} contracts fall inside the 90-day decision window.</Takeaway>
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-[180px_1fr_150px_120px] items-center gap-4 nb-label pb-2 mb-1 border-b" style={{ borderColor: BORDER }}>
              <span>Tool</span>
              <span>Runway</span>
              <span className="text-right">Renews</span>
              <span className="text-right">Used · monthly</span>
            </div>
            <div className="divide-y" style={{ borderColor: BORDER }}>
              {runway.map((g) => {
                const posClamped = Math.min(1, Math.max(0, g.days_to_renewal / 365));
                const inWindow = g.days_to_renewal <= 90;
                return (
                  <Link key={g.id} to={`/apps/${g.id}`} className="grid grid-cols-[180px_1fr_150px_120px] items-center gap-4 h-11 hover:bg-[var(--bg-selected)] transition-colors rounded-md px-1 -mx-1" data-testid={`runway-${g.id}`}>
                    <div className="text-[14px] font-medium text-nb-ink truncate">{g.name}</div>
                    <div className="relative h-2">
                      <div className="absolute inset-0 rounded-full" style={{ background: SUNKEN }} />
                      <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(1 - posClamped) * 100}%`, background: inWindow ? NAVY : KAWAII }} />
                      <span className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white" style={{ left: `calc(${(1 - posClamped) * 100}% - 6px)`, background: inWindow ? NAVY : KAWAII }} />
                    </div>
                    <div className="text-right text-[12px] text-nb-ink2 tnum">{shortDate(g.renewal_date)}<span className="block">{dueLabel(g.days_to_renewal)}</span></div>
                    <div className="text-right text-[12px] text-nb-ink2 tnum">{pct(g.utilisation)} · {money(g.monthly_cost)}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      {/* 6. Seats by tool + Adoption by team */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="seats-by-tool">
          <div className="flex items-center justify-between">
            <SectionTitle>Seats by tool</SectionTitle>
            <SegmentedControl value={seatView} onChange={setSeatView} testid="seat-view"
              options={[{ value: "idle_cost", label: "Idle cost" }, { value: "utilisation", label: "Utilisation" }, { value: "seats", label: "Seats" }]} />
          </div>
          <Takeaway>{idleSeats} idle seats cost {money(idleCost)} a month.</Takeaway>
          <div className="flex items-center gap-4 text-[12px] text-nb-ink2 mb-3">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KAWAII }} /> Active</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: SUNKEN, border: `1px solid #C9C3B2` }} /> Idle</span>
          </div>
          <div className="space-y-2.5">
            {seatRows.map((g) => (
              <div key={g.id} className="flex items-center gap-3">
                <div className="w-[120px] shrink-0 text-[13px] text-nb-ink truncate">{g.name}</div>
                <div className="flex-1 h-3 rounded-full overflow-hidden flex" style={{ width: `${(g.seats_bought / maxSeats) * 100}%` }}>
                  <div style={{ width: `${(g.active_users / g.seats_bought) * 100}%`, background: KAWAII }} />
                  <div style={{ width: `${(g.idle_seats / g.seats_bought) * 100}%`, background: SUNKEN, borderTop: "1px solid #C9C3B2", borderBottom: "1px solid #C9C3B2" }} />
                </div>
                <div className="w-[130px] shrink-0 text-right text-[12px] text-nb-ink2 tnum">{g.idle_seats} idle / {money(g.idle_seat_cost)}/mo</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="adoption-by-team">
          <SectionTitle>Adoption by team</SectionTitle>
          <Takeaway>{selTeam ? `${selTeam.team} is the lowest at ${pct(selTeam.adoption)}, against the firm's ${pct(d.firm_people)}.` : ""}</Takeaway>
          <div className="space-y-2 relative">
            {teams.map((t) => (
              <button key={t.team_id} onClick={() => setSelectedTeam(t.team_id)} className="w-full flex items-center gap-3 group" data-testid={`team-bar-${t.team_id}`}>
                <div className="w-[130px] shrink-0 text-left text-[13px] text-nb-ink truncate">{t.team}</div>
                <div className="flex-1 h-3 rounded-full" style={{ background: SUNKEN }}>
                  {t.group_too_small ? (
                    <div className="h-full rounded-full" style={{ width: "100%", backgroundImage: "repeating-linear-gradient(45deg,#E3DED0,#E3DED0 4px,#F7F3E6 4px,#F7F3E6 8px)" }} />
                  ) : (
                    <div className="h-full rounded-full" style={{ width: pct(t.adoption), background: t.team_id === selTeam?.team_id ? NAVY : KAWAII }} />
                  )}
                </div>
                <div className="w-[48px] shrink-0 text-right text-[13px] text-nb-ink2 tnum">{t.group_too_small ? "—" : pct(t.adoption)}</div>
              </button>
            ))}
            <div className="absolute top-0 bottom-0 border-l border-dashed pointer-events-none" style={{ left: `calc(130px + 12px + (100% - 130px - 12px - 48px - 12px) * ${d.firm_people})`, borderColor: NAVY }} />
          </div>
          {selTeam && !selTeam.group_too_small && (
            <div className="mt-4 rounded-[10px] p-3 text-[13px] text-nb-ink" style={{ background: "var(--brand-silky)" }} data-testid="team-detail-box">
              {selTeam.team}: {selTeam.members_using} of {selTeam.members} people use AI. {selTeam.members - selTeam.members_using} not started yet.
            </div>
          )}
        </Card>
      </div>

      {/* 7. Questionnaire banner */}
      <div className="rounded-2xl p-6 flex items-start gap-4" style={{ background: FLORAL }} data-testid="questionnaire-banner">
        <MessageSquare size={22} strokeWidth={1.75} className="text-nb-ink shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[15px] font-bold text-nb-ink">Usage shows what people do. Questionnaires show why.</div>
          <div className="text-[14px] text-nb-ink mt-0.5" style={{ opacity: 0.85 }}>No responses yet: start with the {topTool?.active_users ?? 5} {topTool?.name ?? "CoCounsel"} users before its renewal.</div>
        </div>
        <Link to="/review" className="h-11 inline-flex items-center px-4 rounded-[10px] bg-nb-surface text-[14px] font-semibold text-nb-ink shrink-0" style={{ border: "1px solid var(--border-control)" }} data-testid="start-template">Start from a template</Link>
      </div>

      {/* 8. Footer */}
      <p className="text-[12px] text-nb-ink2">Figures from the renewal report and team table, last 30 days. Teams with fewer than 5 people are hidden for privacy.</p>
    </div>
  );
}
